import type {AxiosError} from "axios";

export const SHIFT_DOMAIN_ERROR_CODES = {
  BREAK_BEFORE_SCHEDULED_TIME: "break_before_scheduled_time",
  BREAK_EXTENDS_PAST_SHIFT_END: "break_extends_past_shift_end",
  NO_ACTIVE_BREAK: "no_active_break",
} as const;

/** Explicit API `error_code` values that may still be captured on shift/break 4xx. */
/** Intentionally empty — expected shift/break business rules are not GlitchTip defects. */
export const SHIFT_BREAK_GLITCHTIP_CAPTURE_ALLOWLIST_4XX = new Set<string>();

export const SYNTHETIC_ERROR_CODES = {
  NETWORK_ABORTED: "network_aborted",
  NETWORK_OFFLINE: "network_offline",
  REACT_RENDER_ERROR: "react_render_error",
  UNHANDLED_REJECTION: "unhandled_rejection",
  UNHANDLED_PROMISE_REJECTION: "unhandled_promise_rejection",
  REFERENCE_ERROR: "reference_error",
  SERVICE_WORKER_LOAD_FAILED: "service_worker_load_failed",
  REALTIME_AUTH_TIMEOUT: "realtime_auth_timeout",
} as const;

type ApiErrorBody = {error_code?: string; message?: string};

function resolveBreakMessageErrorCode(message: string): string | undefined {
  const lower = message.toLowerCase();
  if (lower.includes("cannot be started before its scheduled time")) {
    return SHIFT_DOMAIN_ERROR_CODES.BREAK_BEFORE_SCHEDULED_TIME;
  }
  if (lower.includes("extend past shift end")) {
    return SHIFT_DOMAIN_ERROR_CODES.BREAK_EXTENDS_PAST_SHIFT_END;
  }
  if (lower.includes("no active break")) {
    return SHIFT_DOMAIN_ERROR_CODES.NO_ACTIVE_BREAK;
  }
  return undefined;
}

function isNetworkFailure(axiosError: AxiosError): boolean {
  return !axiosError.response && Boolean(axiosError.request);
}

function resolveSyntheticErrorCode(error: unknown): string | undefined {
  if (error instanceof ReferenceError) {
    return SYNTHETIC_ERROR_CODES.REFERENCE_ERROR;
  }

  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  const lower = message.toLowerCase();

  if (
    lower.includes("sw.js") ||
    lower.includes("service worker") ||
    lower.includes("serviceworker")
  ) {
    return SYNTHETIC_ERROR_CODES.SERVICE_WORKER_LOAD_FAILED;
  }

  if (
    (lower.includes("socket_id") || lower.includes("pusher")) &&
    lower.includes("timeout")
  ) {
    return SYNTHETIC_ERROR_CODES.REALTIME_AUTH_TIMEOUT;
  }

  if (message === "Rejected" || lower === "error: rejected") {
    return SYNTHETIC_ERROR_CODES.UNHANDLED_PROMISE_REJECTION;
  }

  return undefined;
}

export function resolveErrorCode(error: unknown): string {
  const synthetic = resolveSyntheticErrorCode(error);
  if (synthetic) return synthetic;

  if (error && typeof error === "object" && "response" in error) {
    const axiosError = error as AxiosError<ApiErrorBody>;
    const fromBody = axiosError.response?.data?.error_code;
    if (typeof fromBody === "string" && fromBody.length > 0) return fromBody;

    const messageCode = resolveBreakMessageErrorCode(axiosError.response?.data?.message ?? "");
    if (messageCode) return messageCode;

    if (isNetworkFailure(axiosError)) {
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        return SYNTHETIC_ERROR_CODES.NETWORK_OFFLINE;
      }
      return SYNTHETIC_ERROR_CODES.NETWORK_ABORTED;
    }

    const status = axiosError.response?.status;
    if (typeof status === "number" && status >= 500) return `http_${status}`;
    if (typeof status === "number") return `http_${status}`;
  }

  return SYNTHETIC_ERROR_CODES.UNHANDLED_REJECTION;
}

export function isShiftDomainErrorCode(code: string): boolean {
  return (
    code === SHIFT_DOMAIN_ERROR_CODES.BREAK_BEFORE_SCHEDULED_TIME ||
    code === SHIFT_DOMAIN_ERROR_CODES.BREAK_EXTENDS_PAST_SHIFT_END ||
    code === SHIFT_DOMAIN_ERROR_CODES.NO_ACTIVE_BREAK
  );
}
